import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { exchangeCodeForToken, verifyState, initiateOAuthLogin } from '../services/oauthFlow';
import { privateApiClient, publicApiClient } from '../services/apiClient';
import { storageKeys } from '../config/environment';
import { extractPhoneNumber } from '../services/authService';
import { normalizeCompanies } from '../utils/organizationRoleUtils';
import { toEmailString } from '../utils/toEmailString';

/**
 * Page de callback OAuth qui gère le retour après authentification
 */
const AuthCallback = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Récupérer les paramètres de l'URL
                const searchParams = new URLSearchParams(location.search);
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const errorParam = searchParams.get('error');

                // Vérifier s'il y a une erreur
                if (errorParam) {
                    const errorDescription = searchParams.get('error_description') || 
                                           'Erreur lors de l\'authentification';
                    throw new Error(errorDescription);
                }

                // Vérifier que le code est présent
                if (!code) {
                    throw new Error('Code d\'autorisation manquant');
                }

                // Vérifier le state (sécurité CSRF)
                if (!verifyState(state)) {
                    throw new Error('État de sécurité invalide. Veuillez réessayer.');
                }

                // Échanger le code contre un token
                const redirectUri = `${window.location.origin}/auth/callback`;
                await exchangeCodeForToken(code, redirectUri);

                // Récupérer la solution POS
                const solutionsResponse = await publicApiClient.get('/usr/solutions', {
                    params: { code: 'POS' }
                });
                const solution = solutionsResponse?.data;
                if (!solution?.id) {
                    throw new Error('Solution POS introuvable pour ce compte.');
                }

                // Récupérer les organisations de l'utilisateur
                const companiesResponse = await privateApiClient.get('/usr/companies', {
                    params: {
                        cat: 'all',
                        sol: solution.id,
                        slug: '',
                        pge: 1,
                        lmt: 100
                    }
                });

                const companies = companiesResponse?.data?.contents || [];

                // Récupérer les informations de session utilisateur
                const userSessionResponse = await privateApiClient.get('/usr/users/session');
                const profile = userSessionResponse?.data?.contents || userSessionResponse?.data || {};

                const uniqueCompanies = normalizeCompanies(companies);

                // Stocker les données
                localStorage.setItem(storageKeys.solution, solution.id);
                localStorage.setItem(storageKeys.organizations, JSON.stringify(uniqueCompanies));
                
                // Extraire le numéro de téléphone si c'est un objet
                const phoneNumber = extractPhoneNumber(profile.phone);
                
                localStorage.setItem(storageKeys.user, JSON.stringify({
                    id: profile.id,
                    username: profile.username || phoneNumber || '',
                    name: profile.fullName || profile.name || profile.username || phoneNumber || '',
                    email: toEmailString(profile.email)
                }));

                if (uniqueCompanies.length === 1) {
                    const org = uniqueCompanies[0];
                    if (org.role === 'both') {
                        navigate('/auth/organizations');
                    } else {
                        localStorage.setItem(storageKeys.organisation, JSON.stringify(org));
                        localStorage.setItem(storageKeys.destination, org.id);
                        navigate(org.role === 'cashier' ? '/cashier' : '/dashboard');
                    }
                } else if (uniqueCompanies.length > 1) {
                    navigate('/auth/organizations');
                } else {
                    navigate('/auth/organizations');
                }
            } catch (err) {
                console.error('Erreur lors du callback OAuth:', err);
                
                // Si c'est une erreur invalid_grant, c'est probablement un code déjà utilisé
                // Relancer automatiquement la connexion en arrière-plan
                if (err.message && (err.message.includes('invalid_grant') || err.message.includes('Code d\'authentification'))) {
                    console.warn('Code OAuth invalide ou déjà utilisé, relance automatique de la connexion...');
                    // Nettoyer les données de session pour forcer une nouvelle authentification
                    localStorage.removeItem(storageKeys.session);
                    sessionStorage.clear();
                    // Relancer automatiquement la connexion en arrière-plan
                    try {
                        await initiateOAuthLogin();
                        // Ne pas afficher d'erreur, la relance est en cours
                        return;
                    } catch (relaunchError) {
                        console.error('Erreur lors de la relance automatique:', relaunchError);
                        setError('Erreur lors de la connexion. Veuillez réessayer.');
                        setLoading(false);
                        // Rediriger vers la page de login après 3 secondes
                        setTimeout(() => {
                            navigate('/');
                        }, 3000);
                    }
                } else {
                    setError(err.message || 'Une erreur est survenue lors de l\'authentification');
                    setLoading(false);
                    // Rediriger vers la page de login après 3 secondes
                    setTimeout(() => {
                        navigate('/');
                    }, 3000);
                }
            }
        };

        handleCallback();
    }, [location.search, navigate]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-500 to-amber-600">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
                    <p className="text-white font-semibold text-lg">Authentification en cours...</p>
                    <p className="text-white/80 text-sm mt-2">Veuillez patienter</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-500 to-red-600">
                <div className="text-center bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
                    <div className="text-6xl mb-4">❌</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur d'authentification</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
                    >
                        Retour à la connexion
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default AuthCallback;

